import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

// ─── Proposal B layout ───────────────────────────────────────────────────────

const ProposalBLayout = ({ form, children }) => {
  const primary = form?.primary_color || '#E53E3E';
  const secondary = form?.secondary_color || '#1A1A1A';
  const name = form?.business_name || 'Nombre de la empresa';
  const address = form?.address || '';
  const phone = form?.phone || form?.whatsapp || '';
  const email = form?.email || '';
  const website = form?.website || '';
  const logo = form?.logo_url;
  const footer = form?.footer_text || `${name} — Taller Mecánico Automotriz`;

  return (
    <div style={{ width: '794px', minHeight: '1123px', backgroundColor: '#ffffff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      {/* Top accent line */}
      <div style={{ height: '5px', backgroundColor: primary }} />
      {/* Header */}
      <div style={{ padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {logo ? (
            <img src={logo} alt="Logo" style={{ height: '56px', width: 'auto', objectFit: 'contain' }} />
          ) : (
            <div style={{ width: '56px', height: '56px', backgroundColor: primary, borderRadius: '6px', opacity: 0.2 }} />
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: secondary }}>{name}</div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Taller Mecánico Automotriz</div>
          <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>
            {[phone, email, website]?.filter(Boolean)?.join(' · ')}
          </div>
        </div>
      </div>
      {/* Content */}
      <div style={{ flex: 1, padding: '24px 32px' }}>
        {children || (
          <p style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>Área de contenido del documento</p>
        )}
      </div>
      {/* Footer */}
      <div style={{ borderTop: `2px solid ${primary}`, padding: '10px 32px', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#888' }}>
        <span>{footer}</span>
        {address && <span>{address}</span>}
      </div>
    </div>
  );
};

// ─── Proposal A layout (default generated) ──────────────────────────────────

const ProposalALayout = ({ form, children }) => {
  const primary = form?.primary_color || '#E53E3E';
  const secondary = form?.secondary_color || '#1A1A1A';
  const name = form?.business_name || 'Nombre de la empresa';
  const address = form?.address || '';
  const phone = form?.phone || form?.whatsapp || '';
  const whatsapp = form?.whatsapp || '';
  const email = form?.email || '';
  const website = form?.website || '';
  const logo = form?.logo_url;
  const footer = form?.footer_text || `${name} — Taller Mecánico Automotriz`;

  return (
    <div style={{ width: '794px', minHeight: '1123px', backgroundColor: '#ffffff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      {/* Header band */}
      <div style={{ backgroundColor: primary, padding: '24px 32px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        {logo ? (
          <img src={logo} alt="Logo" style={{ height: '64px', width: 'auto', objectFit: 'contain', borderRadius: '4px' }} />
        ) : (
          <div style={{ width: '64px', height: '64px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', textAlign: 'center', padding: '4px' }}>LOGO</div>
        )}
        <div>
          <h1 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', margin: 0, lineHeight: 1.2 }}>{name}</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', margin: '4px 0 0 0' }}>Taller Mecánico Automotriz</p>
        </div>
      </div>
      {/* Contact row */}
      <div style={{ backgroundColor: '#f8f8f8', borderBottom: `3px solid ${primary}`, padding: '12px 32px', display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: '#444' }}>
        {address && <span><span style={{ color: primary, fontWeight: 'bold' }}>📍</span> {address}</span>}
        {phone && <span><span style={{ color: primary, fontWeight: 'bold' }}>📞</span> {phone}</span>}
        {whatsapp && whatsapp !== phone && <span><span style={{ color: primary, fontWeight: 'bold' }}>💬</span> {whatsapp}</span>}
        {email && <span><span style={{ color: primary, fontWeight: 'bold' }}>✉</span> {email}</span>}
        {website && <span><span style={{ color: primary, fontWeight: 'bold' }}>🌐</span> {website}</span>}
      </div>
      {/* Content */}
      <div style={{ flex: 1, padding: '32px', borderLeft: `4px solid ${primary}`, margin: '24px 32px', borderRadius: '2px', minHeight: '600px', backgroundColor: '#fafafa' }}>
        {children || (
          <>
            <p style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>Área de contenido del documento</p>
            <div style={{ marginTop: '24px', borderTop: '1px dashed #ddd', paddingTop: '16px' }}>
              <p style={{ color: '#bbb', fontSize: '11px' }}>Aquí aparecerá el contenido de cotizaciones, órdenes de trabajo, notas de venta, etc.</p>
            </div>
          </>
        )}
      </div>
      {/* Footer */}
      <div style={{ backgroundColor: secondary, padding: '14px 32px', marginTop: 'auto' }}>
        <p style={{ color: '#ffffff', fontSize: '11px', margin: 0, textAlign: 'center', opacity: 0.9 }}>{footer}</p>
      </div>
    </div>
  );
};

// ─── Custom letterhead overlay layout ───────────────────────────────────────

const CustomLetterheadLayout = ({ form, children }) => {
  return (
    <div style={{ width: '794px', minHeight: '1123px', position: 'relative', fontFamily: 'Arial, sans-serif' }}>
      {/* Background letterhead image */}
      <img
        src={form?.letterhead_url}
        alt="Hoja membretada"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      />
      {/* Content overlay */}
      <div style={{ position: 'relative', zIndex: 1, padding: '180px 48px 80px 48px' }}>
        {children || (
          <p style={{ color: '#555', fontSize: '13px', fontStyle: 'italic' }}>Área de contenido del documento</p>
        )}
      </div>
    </div>
  );
};

// ─── VistaA4Tab ──────────────────────────────────────────────────────────────

const VistaA4Tab = ({ form }) => {
  const hasLetterhead = !!form?.letterhead_url;
  const proposal = form?.selected_proposal;

  const handlePrint = () => window.print();

  const renderLayout = () => {
    if (hasLetterhead) {
      return <CustomLetterheadLayout form={form} />;
    }
    if (proposal === 'propuesta_b') {
      return <ProposalBLayout form={form} />;
    }
    // Default: Proposal A (generated layout)
    return <ProposalALayout form={form} />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Vista previa de hoja membretada</h3>
          <p className="text-sm text-muted-foreground">
            {hasLetterhead
              ? 'Usando tu hoja membretada personalizada como base del documento'
              : proposal && proposal !== 'none'
              ? `Usando ${proposal === 'propuesta_b' ? 'Propuesta B' : 'Propuesta A'} — diseño automático`
              : 'Previsualización en formato A4 (794px)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasLetterhead && (
            <span className="flex items-center gap-1.5 text-xs text-success font-medium bg-success/10 px-2.5 py-1 rounded-full">
              <Icon name="CheckCircle2" size={13} />
              Papelería personalizada activa
            </span>
          )}
          <Button onClick={handlePrint} iconName="Printer" variant="outline">
            Imprimir / Exportar PDF
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div id="letterhead-preview" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.12)', margin: '0 auto', width: '794px' }}>
          {renderLayout()}
        </div>
      </div>
    </div>
  );
};

export default VistaA4Tab;
